import { Color } from '@coast/schemas';
import { isNil, isNumber } from 'lodash';
import { Dispatch, SetStateAction } from 'react';
import { MBIconV2Name } from '@shared/components/image';
import { InputComponent } from '@shared/scenes/workflows/InputComponent';
import { isTagComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { User } from '@shared/types/user';

export type ControlOption = {
  uuid?: string;
  id: string;
  label: string;
  type?: string;
  icon?: MBIconV2Name;
  component?: InputComponent;
};

export type FilterOption = ControlOption & {
  component: InputComponent;
};

export type SortOption = ControlOption & {
  direction?: API.SortDirection;
};

export interface ControlRow<T = ControlOption> {
  onUpdate: (item: T) => void;
  onDelete: (item: T) => void;
}

export type FilterTagOption = {
  uuid?: string;
  label: string;
  value: string | number;
  backgroundColor?: Color | 'white';
  quantity?: number;
  filter?: FilterOption;
  user?: User;
  archived?: boolean;
};

// A PersonOption represents a selectable user within multi-select components.
// For now it shares the same structure as FilterTagOption but is defined as a
// distinct alias so we can use stricter generics in react-select components.
export type PersonOption = FilterTagOption;

// A RelatedCardOption represents a selectable related card within multi-select
// components. It too maps to the same shape but is kept distinct for typing
// clarity.
export type RelatedCardOption = FilterTagOption;

export type FilterTextSelection = {
  type: 'TEXT';
  operator: 'empty' | 'contains';
  value?: string;
};

export const numberOperators = ['EQ', 'LT', 'LTE', 'GT', 'GTE', 'empty'] as const;
export type NumberOperator = (typeof numberOperators)[number];

export type FilterNumberSelection = {
  type: 'NUMBER';
  operator: NumberOperator;
  value: number;
};

export function isNumberOperator(value: string): value is NumberOperator {
  return numberOperators.includes(value as NumberOperator);
}

export const comparisonOperators: API.ComparisonOperator[] = [
  'EQ',
  'GT',
  'GTE',
  'IS_ALL_OF',
  'IS_NONE_OF',
  'IS_ONE_OF',
  'LT',
  'LTE',
  'NEQ',
];

export function isComparisonOperator(value: string): value is API.ComparisonOperator {
  return comparisonOperators.includes(value as API.ComparisonOperator);
}

export function controlOptionToNumberSelection(
  operator: ControlOption,
  value: number,
): FilterNumberSelection | null {
  if (!isNumberOperator(operator.id)) {
    return null;
  }
  return { type: 'NUMBER', operator: operator.id, value };
}

export type FilterTagSelection = {
  type: 'TAG';
  filter: FilterOption;
  value: 'empty' | string;
};

export type FilterPersonSelection = {
  type: 'PERSON';
  filter: FilterOption;
  value: 'empty' | 'currentUser' | number;
};

export type FilterRelatedCardSelection = {
  type: 'RELATED_CARD';
  filter: FilterOption;
  value: 'empty' | string;
  label?: string;
};

export type FilterDateOperator =
  | 'empty'
  | 'today'
  | 'exactly'
  | 'between'
  | 'startsOn'
  | 'endsOn'
  | 'daysBeforeToday'
  | 'daysAfterToday';

export type FilterDateSelection = {
  type: 'DATE';
  operator: FilterDateOperator;
  comparisonOperator?: API.ComparisonOperator;
  duration?: number;
  after?: string | undefined;
  before?: string | undefined;
  // When true, `startsOn`/`endsOn` are expressed relative to today via
  // `comparisonOperator` + signed `duration` (days) rather than absolute
  // `after`/`before`. The absolute bound is derived from `duration` at
  // save/filter time so it recomputes daily.
  isRelative?: boolean;
};

export type FilterSelectionMember =
  | FilterTagSelection
  | FilterPersonSelection
  | FilterRelatedCardSelection;

export type FilterSelection =
  | FilterTextSelection
  | FilterNumberSelection
  | FilterTagSelection[]
  | FilterPersonSelection[]
  | FilterRelatedCardSelection[]
  | FilterDateSelection;

export function isFilterDateSelection(
  selection: FilterSelection | undefined,
): selection is FilterDateSelection {
  return !isNil(selection) && !Array.isArray(selection) && selection.type === 'DATE';
}

export function isFilterTextSelection(
  selection: FilterSelection | undefined,
): selection is FilterTextSelection {
  return !isNil(selection) && !Array.isArray(selection) && selection.type === 'TEXT';
}

export function isFilterNumberSelection(
  selection: FilterSelection | undefined,
): selection is FilterNumberSelection {
  return !isNil(selection) && !Array.isArray(selection) && selection.type === 'NUMBER';
}

export function isFilterTagSelectionMember(
  selection: FilterSelectionMember | undefined,
): selection is FilterTagSelection {
  return !isNil(selection) && selection.type === 'TAG';
}

export function isFilterTagSelectionList(
  selection: FilterSelection | undefined,
): selection is FilterTagSelection[] {
  return Array.isArray(selection) && selection.every(isFilterTagSelectionMember);
}

export function isFilterPersonSelectionMember(
  selection: FilterSelectionMember | undefined,
): selection is FilterPersonSelection {
  return !isNil(selection) && selection.type === 'PERSON';
}

export function isFilterPersonSelectionList(
  selection: FilterSelection | undefined,
): selection is FilterPersonSelection[] {
  return Array.isArray(selection) && selection.every(isFilterPersonSelectionMember);
}

export function isFilterRelatedCardSelectionMember(
  selection: FilterSelectionMember | undefined,
): selection is FilterRelatedCardSelection {
  return !isNil(selection) && selection.type === 'RELATED_CARD';
}

export function isFilterRelatedCardSelectionList(
  selection: FilterSelection | undefined,
): selection is FilterRelatedCardSelection[] {
  return Array.isArray(selection) && selection.every(isFilterRelatedCardSelectionMember);
}

export function isFilterSelectionMemberList(
  selection: FilterSelection | undefined,
): selection is FilterTagSelection[] | FilterPersonSelection[] | FilterRelatedCardSelection[] {
  return Array.isArray(selection);
}

export function tagSelectionToTagOption(selection: FilterTagSelection): FilterTagOption {
  const { component } = selection.filter;
  const tagComponent = component && isTagComponent(component) ? component : undefined;
  if (selection.value === 'empty') {
    return { value: 'empty', label: tagComponent?.label ? `No ${tagComponent.label}` : '' };
  }
  const tagOption = tagComponent?.options?.find(o => o.value === selection.value);
  return {
    value: selection.value,
    label: tagOption?.label ?? '',
    backgroundColor: (tagOption?.backgroundColor ?? undefined) as
      | FilterTagOption['backgroundColor']
      | undefined,
    archived: tagOption?.archived ?? undefined,
    filter: selection.filter,
  };
}

export function relatedCardSelectionToRelatedCardOption(
  selection: FilterRelatedCardSelection,
): RelatedCardOption {
  return {
    value: selection.value,
    label: selection.label ?? '',
    filter: selection.filter,
  };
}

export function tagComponentOptionToFilterTagOption(
  option: API.TagComponentOption,
): FilterTagOption {
  return {
    value: option.value,
    label: option.label,
    // API.Color is a string union matching the Color enum values, cast is required at this boundary
    backgroundColor: option.backgroundColor as Color | undefined,
    archived: option.archived ?? undefined,
  };
}

export function tagOptionToTagSelection(
  option: FilterTagOption,
  filter: FilterOption,
): FilterTagSelection {
  return { type: 'TAG', filter, value: String(option.value) };
}

export function personOptionToPersonSelection(
  option: FilterTagOption,
  filter: FilterOption,
): FilterPersonSelection {
  return {
    type: 'PERSON',
    filter,
    value: isNumber(option.value) ? option.value : (option.value as 'empty' | 'currentUser'),
  };
}

export function relatedCardOptionToRelatedCardSelection(
  option: FilterTagOption,
  filter: FilterOption,
): FilterRelatedCardSelection {
  return { type: 'RELATED_CARD', filter, value: String(option.value), label: option.label };
}

export type GroupFilterSelection =
  | FilterTagSelection[]
  | FilterPersonSelection[]
  | FilterRelatedCardSelection[];

export type FilterSelectionState = {
  filter?: FilterOption;
  group?: FilterOption;
  selection: FilterSelection;
  negate: boolean;
};

export type SortSelectionState = {
  sort: SortOption;
  direction: API.SortDirection;
};

export type FilterSelectionStateGroup = Map<string, FilterSelectionState>;

export type FilterOperation =
  | 'addFilter'
  | 'removeFilter'
  | 'removeAllFilters'
  | 'addSelections'
  | 'updateSelections'
  | 'removeAllSelections';

// Shared callback type for setOption/setFilterOption across filter, group, and sort contexts.
// TSelection defaults to FilterSelection but can be narrowed (e.g. GroupFilterSelection).
export type SetOptionFn<TSelection extends FilterSelection = FilterSelection> = (
  filter: FilterOption,
  operation: FilterOperation,
  selection?: TSelection,
  negate?: boolean,
) => void;

export interface ControlsState {
  filters: Map<string, FilterSelectionState>;
  availableFilters: FilterOption[];
  selectedFilters: FilterOption[];

  sorts: Map<string, SortSelectionState>;
  availableSorts: SortOption[];
  selectedSorts: SortOption[];

  groups: Map<string, FilterSelectionState>[];
  availableGroups: FilterOption[];
  selectedGroups: FilterOption[][];

  search: string | null;

  isModified?: boolean;
  showGroupMenu?: boolean;
}

export interface ControlsActions {
  setFilterOption: (
    filter: FilterOption | undefined,
    operation: FilterOperation,
    selection?: FilterSelection,
    negate?: boolean,
  ) => void;
  setGroupOption: (group?: FilterOption, index?: number, selection?: FilterSelection) => void;
  setSelectedFilters: Dispatch<SetStateAction<FilterOption[]>>;
  setSelectedSorts: Dispatch<SetStateAction<SortOption[]>>;
  setSelectedGroups: Dispatch<SetStateAction<FilterOption[][]>>;
  resetGroups(): void;
  setSearch: Dispatch<SetStateAction<string | null>>;
  revertCollectionCriteria(): void;
  updateViewTemplateCollectionCriteria(): void;
  setShowGroupMenu: Dispatch<SetStateAction<boolean>>;
}
